/**
 * Active Context Storage Utilities
 *
 * Phase 4: Storage utilities defined but NOT used yet
 * Purpose: Provide sessionStorage helpers for Active Context (to be used in Phase 7)
 *
 * Storage Strategy:
 * - Use sessionStorage (not localStorage) so context resets on browser close
 * - This forces re-determination of default context on new session
 * - Survives page refreshes within same session
 *
 * Related Files:
 * - app/src/types/active-context.ts (type definitions)
 * - Phase 7: ActiveContextProvider will use these utilities
 *
 * @see /unifiedIdentityMigration.md (Phase 7 section)
 */

import { ActiveContext } from '@/types/active-context'

const ACTIVE_CONTEXT_KEY = 'lina-active-context'

/**
 * Get the active context from sessionStorage
 *
 * @returns ActiveContext object or null if not set
 *
 * @example
 * ```typescript
 * const context = getActiveContext();
 * if (context?.scope === 'household') {
 *   // Show household UI
 * }
 * ```
 */
export function getActiveContext(): ActiveContext | null {
  try {
    const stored = sessionStorage.getItem(ACTIVE_CONTEXT_KEY)
    if (!stored) {
      return null
    }

    const context: ActiveContext = JSON.parse(stored)

    // Validate structure
    if (!context.scope || !['personal', 'organization'].includes(context.scope)) {
      console.warn('[ActiveContext] Invalid stored context:', context)
      clearActiveContext()
      return null
    }

    return context
  } catch (e) {
    console.error('[ActiveContext] Failed to read from sessionStorage:', e)
    clearActiveContext()
    return null
  }
}

/**
 * Set the active context in sessionStorage
 *
 * @param context - ActiveContext to persist
 *
 * @example
 * ```typescript
 * setActiveContext({
 *   scope: 'household',
 *   scope_id: 'household-uuid',
 *   role: 'owner'
 * });
 * ```
 */
export function setActiveContext(context: ActiveContext): void {
  try {
    // Validate before storing
    if (!context.scope || !['personal', 'organization'].includes(context.scope)) {
      console.error('[ActiveContext] Invalid context:', context)
      return
    }

    sessionStorage.setItem(ACTIVE_CONTEXT_KEY, JSON.stringify(context))
    // console.log('[ActiveContext] Context set:', context.scope, context.scope_id ? `(${context.scope_id})` : '');
  } catch (e) {
    console.error('[ActiveContext] Failed to write to sessionStorage:', e)
  }
}

/**
 * Clear the active context from sessionStorage
 *
 * Use when logging out or when stored context is invalid
 *
 * @example
 * ```typescript
 * // On logout
 * clearActiveContext();
 * ```
 */
export function clearActiveContext(): void {
  try {
    sessionStorage.removeItem(ACTIVE_CONTEXT_KEY)
    // console.log('[ActiveContext] Context cleared');
  } catch (e) {
    console.error('[ActiveContext] Failed to clear sessionStorage:', e)
  }
}

/**
 * Check if user has active context stored
 *
 * @returns true if context exists and is valid
 *
 * @example
 * ```typescript
 * if (!hasActiveContext()) {
 *   // Determine default context
 *   const defaultContext = determineDefaultContext(user);
 *   setActiveContext(defaultContext);
 * }
 * ```
 */
export function hasActiveContext(): boolean {
  return getActiveContext() !== null
}

/**
 * Update specific field in active context without replacing entire object
 *
 * @param updates - Partial ActiveContext with fields to update
 *
 * @example
 * ```typescript
 * // Update just the scope_id
 * updateActiveContext({ scope_id: 'new-household-id' });
 * ```
 */
export function updateActiveContext(updates: Partial<ActiveContext>): void {
  const current = getActiveContext()
  if (!current) {
    console.warn('[ActiveContext] Cannot update - no context set')
    return
  }

  setActiveContext({
    ...current,
    ...updates,
  })
}

/**
 * Create a personal context (default for all users)
 *
 * @returns Personal ActiveContext
 *
 * @example
 * ```typescript
 * const personalContext = createPersonalContext();
 * setActiveContext(personalContext);
 * ```
 */
/**
 * @deprecated Use createPersonalContext from @/types/active-context instead.
 */
export function createPersonalContext(): ActiveContext {
  return {
    id: 'personal:unknown',
    scope: 'personal',
    displayName: 'Personal',
    scopeId: null,
  }
}
